import dayjs from 'dayjs';

// Helpers for tenant-local "yyyy-MM-dd" dates and "HH:mm" times returned by the
// schedule generator. Dates are parsed as local calendar dates (never via
// `new Date('yyyy-MM-dd')`, which is UTC midnight and can shift the day).

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_PATTERN = /^(\d{1,2}):(\d{2})/;

export const DAYS_OF_WEEK = [
  { value: 'Monday', label: 'Monday', short: 'Mon' },
  { value: 'Tuesday', label: 'Tuesday', short: 'Tue' },
  { value: 'Wednesday', label: 'Wednesday', short: 'Wed' },
  { value: 'Thursday', label: 'Thursday', short: 'Thu' },
  { value: 'Friday', label: 'Friday', short: 'Fri' },
  { value: 'Saturday', label: 'Saturday', short: 'Sat' },
  { value: 'Sunday', label: 'Sunday', short: 'Sun' },
];

export const parseLocalDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const match = DATE_PATTERN.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

// '2026-10-01' → 'Thu, Oct 1, 2026'
export const formatLocalDate = (value) => {
  const date = parseLocalDate(value);
  return date ? dayjs(date).format('ddd, MMM D, YYYY') : '';
};

// '2026-10-01' → 'Thu'
export const formatLocalWeekday = (value) => {
  const date = parseLocalDate(value);
  return date ? dayjs(date).format('ddd') : '';
};

// '20:30' → '8:30 pm'
export const formatLocalTime = (value) => {
  if (!value || typeof value !== 'string') return '';
  const match = TIME_PATTERN.exec(value);
  if (!match) return '';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return '';
  return dayjs(new Date(2000, 0, 1, hours, minutes)).format('h:mm a');
};

// '2026-10-01T20:30:00' (tenant-local, no offset) → 'Thu, Oct 1, 2026 · 8:30 pm'
export const formatLocalDateTime = (value) => {
  if (!value || typeof value !== 'string') return '';
  const [datePart, timePart] = value.split('T');
  const date = formatLocalDate(datePart);
  const time = formatLocalTime(timePart || '');
  return [date, time].filter(Boolean).join(' · ');
};

export const formatLocalDateRange = (start, end) => {
  const from = formatLocalDate(start);
  const to = formatLocalDate(end);
  if (from && to) return from === to ? from : `${from} – ${to}`;
  return from || to || '';
};

// Audit timestamps (e.g. createdAt) → 'Thu, Oct 1, 2026'
export const formatTimestampDate = (value) => {
  if (!value || typeof value !== 'string') return '';
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  if (hasZone) {
    const date = dayjs(value);
    return date.isValid() ? date.format('ddd, MMM D, YYYY') : '';
  }
  // The API returns audit timestamps already converted to tenant-local time without an offset,
  // so keep the calendar date as-is rather than re-interpreting it as UTC.
  return formatLocalDate(value);
};

export const getDayShortLabel = (dayOfWeek) =>
  DAYS_OF_WEEK.find((d) => d.value === dayOfWeek)?.short || dayOfWeek || '';
